import type { LoomAuthoringDocument } from "./loomAuthoringModel";
import type { LoomProvisionalUnionPreview } from "./loomProvisionalUnionPreview";

export interface LoomProvisionalUnionRequest {
  document: LoomAuthoringDocument;
  requestId: number;
  revision: number;
  type: "derive";
}

export type LoomProvisionalUnionResponse =
  | {
    preview: LoomProvisionalUnionPreview;
    requestId: number;
    revision: number;
    type: "result";
  }
  | {
    message: string;
    requestId: number;
    revision: number;
    type: "error";
  };

export interface LoomProvisionalUnionRuntime {
  error: string | null;
  preview: LoomProvisionalUnionPreview | null;
  renderedRequestId: number | null;
  renderedRevision: number | null;
  requestId: number;
  requestedRevision: number | null;
  updating: boolean;
  workerState: "idle" | "updating" | "ready" | "error";
}

export interface LoomProvisionalUnionWorkerLike {
  onerror: ((event: { message?: string; preventDefault?: () => void }) => void) | null;
  onmessage: ((event: MessageEvent<LoomProvisionalUnionResponse>) => void) | null;
  postMessage: (message: LoomProvisionalUnionRequest) => void;
  terminate: () => void;
}

export function initialLoomProvisionalUnionRuntime(): LoomProvisionalUnionRuntime {
  return {
    error: null,
    preview: null,
    renderedRequestId: null,
    renderedRevision: null,
    requestId: 0,
    requestedRevision: null,
    updating: false,
    workerState: "idle",
  };
}

export class LoomProvisionalUnionScheduler {
  private current = initialLoomProvisionalUnionRuntime();
  private desiredRequestId = 0;
  private disposed = false;
  private inFlight: LoomProvisionalUnionRequest | null = null;
  private nextRequestId = 0;
  private pending: LoomProvisionalUnionRequest | null = null;

  constructor(
    private readonly worker: LoomProvisionalUnionWorkerLike,
    private readonly onState: (state: LoomProvisionalUnionRuntime) => void,
  ) {
    worker.onmessage = (event) => this.handleResponse(event.data);
    worker.onerror = (event) => {
      event.preventDefault?.();
      this.handleWorkerError(event.message ?? "Provisional union Worker failed");
    };
  }

  request(document: LoomAuthoringDocument): number {
    const request: LoomProvisionalUnionRequest = {
      document,
      requestId: ++this.nextRequestId,
      revision: document.revision,
      type: "derive",
    };
    this.desiredRequestId = request.requestId;
    this.current = {
      ...this.current,
      error: null,
      requestId: request.requestId,
      requestedRevision: request.revision,
      updating: true,
      workerState: "updating",
    };
    if (this.inFlight) this.pending = request;
    else this.dispatch(request);
    this.emit();
    return request.requestId;
  }

  suspend(): void {
    if (!this.inFlight && !this.pending) return;
    this.desiredRequestId = ++this.nextRequestId;
    this.pending = null;
    this.current = { ...this.current, error: null, requestId: this.desiredRequestId, updating: false, workerState: "idle" };
    this.emit();
  }

  dispose(): void {
    this.disposed = true;
    this.pending = null;
    this.worker.onmessage = null;
    this.worker.onerror = null;
    this.worker.terminate();
  }

  private dispatch(request: LoomProvisionalUnionRequest): boolean {
    this.inFlight = request;
    try {
      this.worker.postMessage(request);
      return true;
    } catch (error) {
      const message = typeof error === "object" && error !== null && "message" in error
        ? String(error.message)
        : "Unable to send provisional union Worker request";
      this.handleWorkerError(message);
      return false;
    }
  }

  private handleResponse(response: LoomProvisionalUnionResponse): void {
    if (this.disposed || response.requestId !== this.inFlight?.requestId) return;
    this.inFlight = null;
    const isLatest = response.requestId === this.desiredRequestId;
    if (isLatest && response.type === "result") {
      this.current = {
        ...this.current,
        error: null,
        preview: response.preview,
        renderedRequestId: response.requestId,
        renderedRevision: response.revision,
        workerState: "ready",
      };
    } else if (isLatest && response.type === "error") {
      this.current = { ...this.current, error: response.message, workerState: "error" };
    }
    const pending = this.pending;
    this.pending = null;
    if (pending) {
      if (this.dispatch(pending)) this.current = { ...this.current, updating: true, workerState: "updating" };
    } else {
      this.current = { ...this.current, updating: false };
    }
    this.emit();
  }

  private handleWorkerError(message: string): void {
    if (this.disposed) return;
    this.inFlight = null;
    this.pending = null;
    this.current = { ...this.current, error: message, updating: false, workerState: "error" };
    this.emit();
  }

  private emit(): void {
    if (!this.disposed) this.onState({ ...this.current });
  }
}
